/* Pilatix Product Showcase — premium 3D-illusion CloudCarousel.
   Autoplay = continuous slow angular rotation (museum-turntable feel).
   No CSS rotateY/perspective: cards stay rectangular (no distortion).
   Spring physics drive user-triggered moves; constant ω drives autoplay. */

(function () {
  'use strict';
  if (window.__pltxCcInit) return;
  window.__pltxCcInit = true;

  var instances = new Map();
  var TWO_PI = Math.PI * 2;
  var EPS = 0.0006;

  function lerp(a, b, t) { return a + (b - a) * t; }
  // No easing curve on scale/opacity — Reformed's actual values prove linear t = (sin+1)/2
  // produces the right look (front 1.0, 60° 0.79, 120° 0.36, back 0.15).

  function CloudCarousel(root) {
    this.root = root;
    this.viewport = root.querySelector('.pltx2-cc__viewport');
    this.cards = Array.prototype.slice.call(root.querySelectorAll('[data-cc-card]'));
    this.n = this.cards.length;
    if (!this.n || !this.viewport) return;

    var section = root.closest('.pltx2-cc');
    this.section = section;
    this.activeTitleEl = section ? section.querySelector('[data-cc-active-title]') : null;
    this.activeDescEl = section ? section.querySelector('[data-cc-active-desc]') : null;
    this.captionEl = section ? section.querySelector('.pltx2-cc__caption') : null;
    this.counterCur = section ? section.querySelector('[data-cc-counter-current]') : null;
    this.counterTot = section ? section.querySelector('[data-cc-counter-total]') : null;

    if (this.counterTot) this.counterTot.textContent = String(this.n);

    // Geometry — recomputed on resize
    this.cx = 0; this.cy = 0; this.rx = 0; this.ry = 0;

    // Physics state
    this.current = 0;
    this.target = 0;
    this.velocity = 0;
    this.stepAngle = TWO_PI / this.n;
    this.activeIdx = -1;

    // Gentle spring — premium feel, no overshoot
    this.stiffness = 0.05;
    this.damping = 0.90;

    // Card scale + opacity bounds — note minOpacity uses >= 0 so user can set it to 0
    // (= back-hemisphere cards fade to invisible via t² curve).
    var msAttr = parseFloat(root.getAttribute('data-min-scale'));
    var moAttr = parseFloat(root.getAttribute('data-min-opacity'));
    this.minScale = isFinite(msAttr) && msAttr > 0 ? msAttr : 0.18;
    this.minOpacity = isFinite(moAttr) && moAttr >= 0 ? moAttr : 0;

    // Continuous-rotation autoplay: degrees per second → radians per ms.
    // The schema exposes a separate mobile speed; it is only honoured below
    // the 749px breakpoint the stylesheet uses, and falls back to the desktop
    // value when the merchant leaves it unset.
    var dpsAttr = parseFloat(root.getAttribute('data-rotation-dps'));
    var dpsMobileAttr = parseFloat(root.getAttribute('data-rotation-dps-m'));
    if (
      isFinite(dpsMobileAttr) &&
      dpsMobileAttr >= 0 &&
      window.matchMedia('(max-width: 749px)').matches
    ) {
      dpsAttr = dpsMobileAttr;
    }
    var dps = (isFinite(dpsAttr) && dpsAttr >= 0) ? dpsAttr : 8;
    // Negative because we want target (which is -i*step for i-th card front) to decrease over time,
    // visually rotating the array forward.
    this.autoOmega = -(dps * Math.PI / 180) / 1000; // rad / ms

    // Drag state
    this.dragging = false;
    this.dragId = null;
    this.dragStartX = 0;
    this.dragStartTarget = 0;
    this.lastDragX = 0;
    this.lastDragT = 0;
    this.dragVelocity = 0;

    // Interaction pause
    this.userPauseUntil = 0;
    this.AUTO_PAUSE_MS = 4000;

    // Loop control
    this.rafId = null;
    this.lastFrameT = 0;
    this.visible = true;

    this.tick = this.tick.bind(this);
    this.onResize = this.onResize.bind(this);
    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);

    this.attach();
    this.measure();
    this.target = 0;
    this.current = 0;
    this.layout();
    this.startLoop();
    this.setupVisibilityObserver();
  }

  CloudCarousel.prototype.attach = function () {
    this.resizeObs = ('ResizeObserver' in window) ? new ResizeObserver(this.onResize) : null;
    if (this.resizeObs) this.resizeObs.observe(this.viewport); else window.addEventListener('resize', this.onResize);

    this.viewport.addEventListener('pointerdown', this.onPointerDown);

    var self = this;
    this.cards.forEach(function (card, i) {
      card.addEventListener('click', function (e) {
        // If a real drag occurred, suppress click
        if (self._dragMoved) { self._dragMoved = false; return; }
        if (i !== self.activeIdx) {
          self.userPauseUntil = performance.now() + self.AUTO_PAUSE_MS;
          self.snapTo(i);
        }
      });
    });
  };

  CloudCarousel.prototype.detach = function () {
    if (this.resizeObs) this.resizeObs.disconnect(); else window.removeEventListener('resize', this.onResize);
    this.viewport.removeEventListener('pointerdown', this.onPointerDown);
    window.removeEventListener('pointermove', this.onPointerMove);
    window.removeEventListener('pointerup', this.onPointerUp);
    window.removeEventListener('pointercancel', this.onPointerUp);
    if (this.rafId) cancelAnimationFrame(this.rafId);
    if (this.visObs) this.visObs.disconnect();
  };

  CloudCarousel.prototype.setupVisibilityObserver = function () {
    if (!('IntersectionObserver' in window)) return;
    var self = this;
    this.visObs = new IntersectionObserver(function (entries) {
      var visible = entries[0].isIntersecting;
      self.visible = visible;
      if (visible) self.startLoop(); else self.stopLoop();
    }, { threshold: 0.01 });
    this.visObs.observe(this.root);
  };

  CloudCarousel.prototype.measure = function () {
    var r = this.viewport.getBoundingClientRect();
    if (r.width <= 0 || r.height <= 0) return;
    this.cx = r.width / 2;
    this.cy = r.height / 2;
    // Narrow stages (phones): wider spread + the layout() scale-gamma below
    // give the center card a real "pop" without leaving the screen.
    this.isNarrow = r.width < 700;
    this.rx = r.width * (this.isNarrow ? 0.44 : 0.40);
    // Bigger ry — back-half cards lift visibly above front-half ones at same x. Without this,
    // ellipse symmetry parks them at the same x with only ~40px vertical separation, and the
    // back card peeks behind the front-side neighbor → overlap.
    this.ry = r.height * 0.10;
  };

  CloudCarousel.prototype.onResize = function () {
    this.measure();
    this.layout();
  };

  // --- Loop -------------------------------------------------------------

  CloudCarousel.prototype.startLoop = function () {
    if (this.rafId !== null) return;
    this.lastFrameT = performance.now();
    this.rafId = requestAnimationFrame(this.tick);
  };

  CloudCarousel.prototype.stopLoop = function () {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  };

  CloudCarousel.prototype.tick = function (now) {
    this.rafId = null;
    if (!this.visible) return;

    var dt = Math.min(48, now - this.lastFrameT); // ms; cap to avoid huge jumps after backgrounding
    this.lastFrameT = now;

    if (this.dragging) {
      // Direct: current snaps to target while user drags
      this.current = this.target;
      this.velocity = 0;
    } else {
      // Should autoplay run? Only when no recent user interaction and ω != 0.
      var canAutoplay = this.autoOmega !== 0 && now >= this.userPauseUntil;

      if (canAutoplay) {
        // Move target at constant angular velocity → continuous, premium rotation.
        this.target += this.autoOmega * dt;
      }

      // Spring toward target — gentle.
      var diff = this.target - this.current;
      var force = diff * this.stiffness;
      this.velocity = (this.velocity + force) * this.damping;
      this.current += this.velocity;

      // Settle
      if (!canAutoplay && Math.abs(this.velocity) < EPS && Math.abs(diff) < EPS) {
        this.current = this.target;
        this.velocity = 0;
      }
    }

    this.layout();
    this.rafId = requestAnimationFrame(this.tick);
  };

  // --- Layout per frame -------------------------------------------------

  CloudCarousel.prototype.layout = function () {
    var step = this.stepAngle;
    var minS = this.minScale;
    var minO = this.minOpacity;
    var rx = this.rx, ry = this.ry;

    // Active card detection (closest to "front" = sin(angle) === 1)
    var bestSin = -2, bestIdx = 0;
    // Motion blur from current angular speed (pixels/frame velocity → blur)
    var speed = Math.min(0.8, Math.abs(this.velocity) * 4);

    for (var i = 0; i < this.n; i++) {
      // Rotate the array by `current`. Front happens when (i*step + π/2 + current) === π/2.
      var a = i * step + Math.PI / 2 + this.current;
      var s = Math.sin(a);
      var c = Math.cos(a);

      // t in [0, 1]: 0 = full back, 1 = full front
      var t = (s + 1) * 0.5;

      // LINEAR scale + opacity — matches Reformed exactly. With odd N (7 cards), no two
      // cards share the same x position, so symmetric-pair stacking is geometrically impossible.
      // Adjacent cards on the same side WILL overlap by design — that's the 3D peek-behind effect.
      // Narrow stages: gamma on t steepens the falloff so the CENTER card pops
      // (neighbors ~0.7x instead of ~0.85x) while side cards shrink into the spread.
      var tS = this.isNarrow ? Math.pow(t, 2.2) : t;
      var scale = minS + (1 - minS) * tS;
      var opacity = lerp(minO, 1, t);

      var x = rx * c;
      var y = ry * s;

      // Depth-of-field blur: front sharp, back soft. Plus tiny motion blur during fast spin.
      var dofBlur = (1 - t) * 0.7;
      var blur = Math.min(1.6, dofBlur + speed * 0.4);

      var card = this.cards[i];
      card.style.setProperty('--x', x.toFixed(2) + 'px');
      card.style.setProperty('--y', y.toFixed(2) + 'px');
      card.style.setProperty('--s', scale.toFixed(4));
      card.style.setProperty('--o', opacity.toFixed(3));
      card.style.zIndex = String(Math.round(scale * 100));
      card.style.filter = blur > 0.04 ? 'blur(' + blur.toFixed(2) + 'px)' : '';

      if (s > bestSin) { bestSin = s; bestIdx = i; }
    }

    // Update active card / caption (only when the carousel is roughly settled — avoids flicker
    // during continuous autoplay; we'll still update at each natural transition point).
    var velAbs = Math.abs(this.velocity);
    if (bestIdx !== this.activeIdx) {
      // Fire when velocity is low OR when bestSin is close enough to 1 that this is a "real" front
      if (velAbs < 0.012 || bestSin > 0.985) {
        this.setActive(bestIdx);
      }
    } else if (this.activeIdx === -1) {
      this.setActive(bestIdx);
    }
  };

  CloudCarousel.prototype.setActive = function (idx) {
    if (idx === this.activeIdx) return;
    this.activeIdx = idx;
    var card = this.cards[idx];
    if (!card) return;
    var title = card.getAttribute('data-title') || '';
    var desc = card.getAttribute('data-desc') || '';

    var self = this;
    if (this.captionEl && (this.activeTitleEl || this.activeDescEl)) {
      this.captionEl.classList.add('is-fading');
      setTimeout(function () {
        if (self.activeTitleEl) self.activeTitleEl.textContent = title;
        if (self.activeDescEl) self.activeDescEl.textContent = desc;
        requestAnimationFrame(function () { self.captionEl.classList.remove('is-fading'); });
      }, 160);
    }
    if (this.counterCur) this.counterCur.textContent = String(idx + 1);
  };

  // --- Drag --------------------------------------------------------------

  CloudCarousel.prototype.onPointerDown = function (e) {
    if (e.button !== undefined && e.button !== 0) return;
    if (e.target.closest('a')) return;
    this.dragging = true;
    this.dragId = e.pointerId;
    this.dragStartX = e.clientX;
    this.dragStartTarget = this.target;
    this.lastDragX = e.clientX;
    this.lastDragT = performance.now();
    this.dragVelocity = 0;
    this._dragMoved = false;
    this.userPauseUntil = performance.now() + this.AUTO_PAUSE_MS;
    try { this.viewport.setPointerCapture(e.pointerId); } catch (err) {}
    this.root.classList.add('is-dragging');
    window.addEventListener('pointermove', this.onPointerMove);
    window.addEventListener('pointerup', this.onPointerUp);
    window.addEventListener('pointercancel', this.onPointerUp);
  };

  CloudCarousel.prototype.onPointerMove = function (e) {
    if (!this.dragging || e.pointerId !== this.dragId) return;
    var dx = e.clientX - this.dragStartX;
    if (Math.abs(dx) > 4) this._dragMoved = true;
    // 3D carousel: content follows finger regardless of RTL — no direction inversion.
    var dragScale = Math.max(280, this.rx * 1.4);
    var deltaAngle = dx / dragScale;
    this.target = this.dragStartTarget - deltaAngle;
    var now = performance.now();
    var dt = Math.max(8, now - this.lastDragT);
    var dPosX = e.clientX - this.lastDragX;
    this.dragVelocity = dPosX / dt;
    this.lastDragX = e.clientX;
    this.lastDragT = now;
    e.preventDefault();
  };

  CloudCarousel.prototype.onPointerUp = function (e) {
    if (!this.dragging || (e.pointerId !== this.dragId && e.pointerId !== undefined)) return;
    this.dragging = false;
    this.dragId = null;
    this.root.classList.remove('is-dragging');
    window.removeEventListener('pointermove', this.onPointerMove);
    window.removeEventListener('pointerup', this.onPointerUp);
    window.removeEventListener('pointercancel', this.onPointerUp);

    // Gentler momentum — premium feel, no overshoot or twitch.
    var momentumPx = this.dragVelocity * 90;
    var momentumRad = momentumPx / Math.max(280, this.rx * 1.4);
    var momentumTarget = this.target - momentumRad;

    var idx = Math.round(-momentumTarget / this.stepAngle);
    idx = ((idx % this.n) + this.n) % this.n;
    this.userPauseUntil = performance.now() + this.AUTO_PAUSE_MS;
    this.snapTo(idx);
  };

  // --- Snap --------------------------------------------------------------

  CloudCarousel.prototype.snapTo = function (idx) {
    idx = ((idx % this.n) + this.n) % this.n;
    var raw = -idx * this.stepAngle;
    // Choose closest equivalent target (no spinning the long way around)
    while (raw - this.current > Math.PI) raw -= TWO_PI;
    while (this.current - raw > Math.PI) raw += TWO_PI;
    this.target = raw;
  };

  // --- Init / lifecycle -------------------------------------------------

  function init(stage) {
    if (!stage || instances.has(stage)) return;
    var inst = new CloudCarousel(stage);
    if (inst.viewport) instances.set(stage, inst);
  }

  function cleanup(stage) {
    var inst = instances.get(stage);
    if (!inst) return;
    inst.detach();
    instances.delete(stage);
  }

  function initAll() {
    var stages = document.querySelectorAll('[data-pltx-cc]');
    for (var i = 0; i < stages.length; i++) init(stages[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll, { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var stage = e.target.querySelector ? e.target.querySelector('[data-pltx-cc]') : null;
    if (stage) init(stage);
  });

  document.addEventListener('shopify:section:unload', function (e) {
    var stage = e.target.querySelector ? e.target.querySelector('[data-pltx-cc]') : null;
    if (stage) cleanup(stage);
  });

  document.addEventListener('shopify:block:select', function (e) {
    var blk = e.target;
    if (!blk) return;
    var stage = blk.closest('[data-pltx-cc]');
    if (!stage) return;
    var inst = instances.get(stage);
    if (!inst) return;
    var idx = parseInt(blk.getAttribute('data-cc-index'), 10);
    if (isFinite(idx)) {
      inst.userPauseUntil = performance.now() + 60000; // pause autoplay during editing
      inst.snapTo(idx);
    }
  });
})();
